import { useCallback, useMemo } from 'react';

import { useStoreContext } from 'features/store';
import { isEqual } from 'lodash-es';
import { useIntl } from 'react-intl';

import { ICartEntry, ICombo } from '@rbi-ctg/menu';
import { toaster } from 'components/toaster/toaster';
import { useHydrateCartMutation } from 'generated/graphql-gateway';
import { CustomEventNames, EventTypes, logAddOrRemoveFromCart, logEvent } from 'state/amplitude';
import type { IAddToCartSelectionAttributes } from 'state/amplitude/types';
import { useAuthContext } from 'state/auth';
import { DISCOUNT_OFFER_CART_ID } from 'state/cart';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { removeAppliedOffersInStorage } from 'state/global-state/models/loyalty/offers/offers.utils';
import { removeAppliedRewardsInStorage } from 'state/global-state/models/loyalty/rewards/rewards.utils';
import { useGetAvailableRewards } from 'state/loyalty/hooks/use-get-available-rewards';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { useMenuContext } from 'state/menu';
import { useServiceModeContext } from 'state/service-mode';
import { computeCartTotal } from 'utils/cart/helper';
import { isHydratedReward, mapLegacyToMinimalCartEntries } from 'utils/minimal-cart';
import { routes } from 'utils/routing';

import { useRoute } from './navigation/use-route';
import { useEnableUnifiedMenu } from './use-enable-unified-menu';
import { hydrateEntries } from './use-minimal-cart';
import { useRepriceCartEntries } from './use-reprice-cart-entries';

interface IUseCart {
  cartEntries: ICartEntry[];
  calculateCartTotal: () => number;
  calculateCartTotalWithDiscount: (
    paramCartEntries?: ICartEntry[]
  ) => { cartTotal: number; isCartTotalNegative: boolean };
  addItemToCart: (
    cartEntry: ICartEntry,
    selectionAttrs?: IAddToCartSelectionAttributes | undefined,
    showMessage?: boolean,
    fromHydration?: boolean
  ) => void;
  emptyCart: () => void;
  isCartEmpty: boolean;
  logCartEntryRemovedFromCart: (cartEntry: ICartEntry) => void;
  removeAllFromCart: (cartEntries: ICartEntry[]) => void;
  removeFromCart: (objectWithCartId: { cartId: string }) => void;
  repriceCartEntries: (
    entries: ICartEntry[],
    parentEntry?: ICartEntry,
    mainCombo?: ICombo
  ) => ICartEntry[];
  updateCartEntry: (
    newCartEntry: ICartEntry,
    cartId: string,
    skipShowMessage?: boolean,
    fromHydration?: boolean
  ) => void;
  updateQuantity: (cartId: string, quantity: number) => void;
  numberOfItemsInCart: number;
}

/**
 * Expose the cart state and the utility methods to mutate it.
 *
 * When an action to mutate the cart is triggered, it manages the Redux cart actions, logs the events,
 * coordinates with other parts of the code, and dispatches actions on related slices.
 */
export const useCart = (): IUseCart => {
  const dispatch = useAppDispatch();
  const cartEntries = useAppSelector(selectors.ordering.selectCartEntries);
  const minimalCartEntries = useAppSelector(selectors.ordering.selectMinimalCartEntries);
  const { loyaltyUser } = useLoyaltyUser();
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const discountAppliedCmsOffers = useAppSelector(selectors.loyalty.selectDiscountAppliedCmsOffer);

  const { pathname } = useRoute();

  const shouldDisplayMenuToast = !!pathname.startsWith(routes.menu);

  const { formatMessage } = useIntl();

  const { pricingFunction } = useMenuContext();

  const loyaltyEnabled = useIsLoyaltyEnabled();
  const { getAvailableRewardFromCartEntry } = useGetAvailableRewards();

  const isUnifiedMenuEnabled = useEnableUnifiedMenu();

  const { store } = useStoreContext();
  const storeId = store?.number;
  const { serviceMode } = useServiceModeContext();
  const { isAuthenticated } = useAuthContext();
  const [hydrateCart] = useHydrateCartMutation();

  const updateCartEntry = useCallback(
    (
      newCartEntry: ICartEntry,
      cartId: string,
      skipShowMessage?: boolean,
      fromHydration?: boolean
    ) => {
      const originalEntry = cartEntries.find(entry => entry.cartId === cartId);
      if (!originalEntry) {
        return;
      }

      dispatch(
        actions.ordering.updateCartEntry({
          cartEntry: { ...newCartEntry, ...(fromHydration ? { image: originalEntry.image } : {}) },
          cartId,
        })
      );

      // TODO: analyze moving this to extraReducers on loyalty slice
      // If the original entry has a reward applied but the new entry is a different item,
      // then remove the reward if it is not a picker selection.
      const isPickerSelection = Object.keys(originalEntry.pickerSelections).length > 0;
      const appliedReward = appliedLoyaltyRewards[originalEntry?.cartId];
      if (
        !isPickerSelection &&
        appliedReward &&
        appliedReward.rewardBenefitId !== newCartEntry._id &&
        !isHydratedReward(newCartEntry)
      ) {
        dispatch(
          actions.loyalty.removeAppliedReward({
            rewardBenefitId: appliedReward.rewardBenefitId,
            cartId: originalEntry.cartId,
          })
        );
      }

      logAddOrRemoveFromCart({
        action: 'remove',
        cartEntry: originalEntry,
        previousCartEntries: [originalEntry],
        isReward: !!appliedReward,
      });
      logAddOrRemoveFromCart({
        action: 'add',
        cartEntry: newCartEntry,
        previousCartEntries: [originalEntry],
      });

      if (shouldDisplayMenuToast && !skipShowMessage) {
        return setTimeout(() => {
          return toaster.addToCartToast(
            `${formatMessage({ id: 'updateCartSuccess' }, { itemName: newCartEntry.name })}`,
            newCartEntry.image
          );
        }, 100);
      }

      if (!skipShowMessage) {
        return toaster.positive(
          formatMessage({ id: 'updateCartSuccess' }, { itemName: newCartEntry.name })
        );
      }

      return;
    },
    [appliedLoyaltyRewards, cartEntries, dispatch, formatMessage, shouldDisplayMenuToast]
  );

  // Adds a single cartEntry to the cart
  const addItemToCart = useCallback<IUseCart['addItemToCart']>(
    (cartEntry, selectionAttrs, showMessage = true, fromHydration = false) => {
      if (isUnifiedMenuEnabled && !fromHydration) {
        if (!storeId || !serviceMode || !isAuthenticated) {
          return;
        }
        const minimalCartEntries = mapLegacyToMinimalCartEntries([cartEntry]);
        hydrateEntries(
          minimalCartEntries,
          storeId,
          serviceMode,
          hydrateCart,
          addItemToCart,
          updateCartEntry,
          dispatch
        );
        if (showMessage) {
          toaster.addToCartToast(
            `${formatMessage({ id: 'addToCartSuccess' }, { itemName: cartEntry.name })}`,
            cartEntry.image
          );
        }
        return;
      }

      logAddOrRemoveFromCart({
        action: 'add',
        cartEntry,
        previousCartEntries: cartEntries,
        selectionAttrs,
      });

      dispatch(actions.ordering.addCartEntry(cartEntry));

      if (cartEntry.isDonation) {
        return;
      }

      if (showMessage) {
        // Menu Toast
        if (shouldDisplayMenuToast) {
          return toaster.addToCartToast(
            `${formatMessage({ id: 'addToCartSuccess' }, { itemName: cartEntry.name })}`,
            cartEntry.image
          );
        }

        // Regular Toast
        return toaster.positive(
          formatMessage({ id: 'addToCartSuccess' }, { itemName: cartEntry.name })
        );
      }
    },
    [
      cartEntries,
      dispatch,
      formatMessage,
      hydrateCart,
      isAuthenticated,
      isUnifiedMenuEnabled,
      serviceMode,
      shouldDisplayMenuToast,
      storeId,
      updateCartEntry,
    ]
  );

  const logCartEntryRemovedFromCart = useCallback(
    (cartEntry: ICartEntry) => {
      const appliedReward = appliedLoyaltyRewards[cartEntry?.cartId];
      logAddOrRemoveFromCart({
        action: 'remove',
        cartEntry,
        previousCartEntries: cartEntries,
        isReward: !!appliedReward,
      });
      if (cartEntry.isUpsell) {
        logEvent(CustomEventNames.UPSELL_REMOVED, EventTypes.Other, {
          Id: cartEntry._id,
          Name: cartEntry.name,
          Price: cartEntry.price && cartEntry.price / 100,
        });
      }
    },
    [appliedLoyaltyRewards, cartEntries]
  );

  const removeRewardIfNeeded = useCallback(
    (cartEntry: ICartEntry) => {
      const { cartId } = cartEntry;
      const cartEntryReward = getAvailableRewardFromCartEntry(cartEntry);

      const isRewardApplied = !!appliedLoyaltyRewards?.[cartId]?.timesApplied;
      if (loyaltyEnabled && isRewardApplied && cartEntryReward) {
        dispatch(
          actions.loyalty.removeAppliedReward({
            rewardBenefitId: cartEntryReward.rewardBenefitId,
            cartId,
          })
        );
      }
    },
    [appliedLoyaltyRewards, getAvailableRewardFromCartEntry, loyaltyEnabled, dispatch]
  );

  // Removes a single cartEntry using its cartId
  const removeFromCart = useCallback<IUseCart['removeFromCart']>(
    ({ cartId }: { cartId: string }) => {
      dispatch(actions.ordering.removeCartEntries(new Set([cartId])));
      dispatch(actions.loyalty.removeAppliedOfferByCartEntry({ cartId } as ICartEntry));
      const cartEntryToRemove = cartEntries.find(entry => entry.cartId === cartId);

      if (!cartEntryToRemove) {
        return;
      }

      // removes applied rewards associated to cart entry on removal
      removeRewardIfNeeded(cartEntryToRemove);
      logCartEntryRemovedFromCart(cartEntryToRemove);
    },
    [cartEntries, dispatch, logCartEntryRemovedFromCart, removeRewardIfNeeded]
  );

  // Removes all provided cartEntries from the cart using their cartIds
  const removeAllFromCart = useCallback<IUseCart['removeAllFromCart']>(
    (cartEntriesToRemove = []) => {
      const cartEntryIdsToRemove = new Set(
        cartEntriesToRemove.map((entry: ICartEntry) => entry.cartId)
      );
      dispatch(actions.ordering.removeCartEntries(cartEntryIdsToRemove));

      cartEntriesToRemove.forEach(cartEntry => {
        removeRewardIfNeeded(cartEntry);
        dispatch(actions.loyalty.removeAppliedOfferByCartEntry(cartEntry));
        logCartEntryRemovedFromCart(cartEntry);
      });
    },
    [dispatch, logCartEntryRemovedFromCart, removeRewardIfNeeded]
  );

  const updateQuantity = useCallback<IUseCart['updateQuantity']>(
    (cartId: string, quantity: number) => {
      const cartEntryToUpdate = cartEntries.find(cartEntry => cartEntry.cartId === cartId);
      if (!cartEntryToUpdate) {
        return;
      }

      if (quantity < 1) {
        return removeFromCart({ cartId });
      }
      const price = pricingFunction({ item: cartEntryToUpdate, quantity });
      dispatch(actions.ordering.updateQuantityAndPrice({ cartId, quantity, price }));
    },
    [cartEntries, dispatch, pricingFunction, removeFromCart]
  );

  const repriceCartEntriesHelper = useRepriceCartEntries();
  const repriceCartEntries = useCallback(
    (entries: ICartEntry[], parentEntry?: ICartEntry, mainCombo?: ICombo) => {
      const newEntries = repriceCartEntriesHelper(entries, parentEntry, mainCombo);
      // If newEntries is structurally equal to entries, then return original object
      // to avoid unnecessary re-renders. This is a fix to priceOrder mutation being called twice.
      return isEqual(entries, newEntries) ? entries : newEntries;
    },
    [repriceCartEntriesHelper]
  );

  const emptyCart = useCallback(() => {
    dispatch(actions.ordering.resetCart());

    removeAppliedOffersInStorage();
    removeAppliedRewardsInStorage();

    // TODO: evaluate moving this to `extraReducers` on loyalty slices
    if (loyaltyEnabled) {
      dispatch(actions.loyalty.resetAppliedOffers());
      dispatch(
        actions.loyalty.resetLoyaltyRewardsState({
          points: loyaltyUser?.points ?? 0,
          shouldResetAvailableRewardsMap: false,
        })
      );
    }
  }, [loyaltyEnabled, loyaltyUser, dispatch]);

  const numberOfItemsInCart = useMemo(
    () => countCartItems(cartEntries) + countCartItems(minimalCartEntries),
    [cartEntries, minimalCartEntries]
  );

  const isCartEmpty = !cartEntries?.length;

  const calculateCartTotalWithDiscount = useCallback(
    (paramCartEntries?: ICartEntry[]) => {
      const total = computeCartTotal(paramCartEntries || cartEntries, {
        loyaltyEnabled,
        appliedLoyaltyRewards,
        appliedLoyaltyOfferDiscount: discountAppliedCmsOffers?.incentives?.[0] as any,
      });
      return {
        cartTotal: total,
        isCartTotalNegative: total < 0,
      };
    },
    [appliedLoyaltyRewards, cartEntries, loyaltyEnabled, discountAppliedCmsOffers]
  );

  const calculateCartTotal = useCallback(() => {
    const { cartTotal, isCartTotalNegative } = calculateCartTotalWithDiscount(cartEntries);
    return isCartTotalNegative ? 0 : cartTotal;
  }, [calculateCartTotalWithDiscount, cartEntries]);

  return {
    addItemToCart,
    cartEntries,
    calculateCartTotal,
    calculateCartTotalWithDiscount,
    emptyCart,
    isCartEmpty,
    logCartEntryRemovedFromCart,
    removeAllFromCart,
    repriceCartEntries,
    removeFromCart,
    updateCartEntry,
    updateQuantity,
    numberOfItemsInCart,
  };
};

function countCartItems(cartEntries: Array<{ cartId?: string; quantity?: number }>) {
  return cartEntries.reduce(
    (total, cartEntry) =>
      total + (cartEntry.cartId === DISCOUNT_OFFER_CART_ID ? 0 : cartEntry.quantity ?? 1),
    0
  );
}
